import { IObservable, Subject } from '~/rage/utils';
import {
	IAdminMarkModerationRequestsViewed,
	VehicleNumberPlate,
	VehicleNumberPlateType,
	WarnReason,
} from '~/shared';
import {
	IAdminService,
	IAnticheatRequest,
	INicknameRequest,
	INumberplateRequest,
} from './adminServiceTypes';

const STUB_REQUEST_LIST_SIZE = 15;

const stubNicknamePairs = [
	{ current: 'Artem Bakhia', next: 'Artem Babakhia' },
	{ current: 'Max Volla', next: 'Max мессенджер' },
	{ current: 'Vlad Jevico', next: 'Vlad Burger' },
	{ current: 'Dmitrii Stepanov', next: 'Stepan Dmitriiev' },
] as const;

const stubVehicleName = 'MB E63S V213';

const stubCurrentNumberplate: VehicleNumberPlate = {
	type: VehicleNumberPlateType.ClassicNoFlag,
	text: 'A777AA',
	region: '77',
};

const stubWantedNumberplate: VehicleNumberPlate = {
	type: VehicleNumberPlateType.Custom,
	text: 'CCDPLANET',
	region: null,
};

const mockNumberplateRequests: INumberplateRequest[] = Array.from(
	{ length: STUB_REQUEST_LIST_SIZE },
	(_, i) => {
		const n = i + 1;
		const pair = stubNicknamePairs[i % stubNicknamePairs.length];
		return {
			id: `np-stub-${n}`,
			requestNumber: 52000 + i,
			playerId: 40000 + i,
			playerName: pair.current,
			vehicleName: stubVehicleName,
			vehicleEntityId: 100 + i,
			currentNumberplate: { ...stubCurrentNumberplate },
			numberplate: { ...stubWantedNumberplate },
		};
	},
)
	.reverse()
	.map((request, index) => ({ ...request, requestNumber: index + 1 }));

const mockNicknameRequests: INicknameRequest[] = Array.from(
	{ length: STUB_REQUEST_LIST_SIZE },
	(_, i) => {
		const n = i + 1;
		const pair = stubNicknamePairs[i % stubNicknamePairs.length];
		return {
			id: `nn-stub-${n}`,
			viewId: `nn-stub-${n}:1`,
			requestNumber: 53000 + i,
			playerId: 45000 + i,
			currentNickname: pair.current,
			newNickname: pair.next,
		};
	},
)
	.reverse()
	.map((request, index) => ({ ...request, requestNumber: index + 1 }));

const stubAnticheatReasons = [
	WarnReason.TELEPORT_TO_COORDS,
	WarnReason.ARMOUR_HACK,
	WarnReason.VEHICLE_SPEEDHACK,
	WarnReason.WEAPON,
];
const stubAnticheatDetails = [
	{ actual: 94.3, limit: 40 },
	{ from: 25, to: 100 },
	{ actual: 72, limit: 55 },
	{ weapon: 'WEAPON_PISTOL' },
];

const mockAnticheatRequests: IAnticheatRequest[] = Array.from(
	{ length: STUB_REQUEST_LIST_SIZE },
	(_, i) => {
		const n = i + 1;
		const pair = stubNicknamePairs[i % stubNicknamePairs.length];
		return {
			id: `ac-stub-${n}`,
			characterId: String(472022 + i),
			requestNumber: 34032 + i,
			accountId: `${472022 + i}`,
			playerName: pair.current,
			online: i % 2 === 0,
			reason: stubAnticheatReasons[i % stubAnticheatReasons.length],
			details: stubAnticheatDetails[i % stubAnticheatDetails.length],
			time: Date.now() - 3_600,
		};
	},
)
	.reverse()
	.map((request, index) => ({ ...request, requestNumber: index + 1 }));

export class AdminServiceStub implements IAdminService {
	private reportsCount = new Subject<number>(0);
	private moderationsCount = new Subject<number>(0);
	private anticheatCount = new Subject<number>(mockAnticheatRequests.length);
	private numberplateRequests = new Subject<INumberplateRequest[]>(
		mockNumberplateRequests,
	);
	private nicknameRequests = new Subject<INicknameRequest[]>(
		mockNicknameRequests,
	);
	private anticheatRequests = new Subject<IAnticheatRequest[]>(
		mockAnticheatRequests,
	);
	private screenshot = new Subject<string | null>(null);
	private viewedAnticheatRequestIds = new Set<string>();
	private viewedModerationRequestIds = new Set<string>();

	constructor() {
		this.syncModerationsCount();
	}

	public getReportsCount(): IObservable<number> {
		return this.reportsCount;
	}

	public getModerationsCount(): IObservable<number> {
		return this.moderationsCount;
	}

	public getAnticheatCount(): IObservable<number> {
		return this.anticheatCount;
	}

	public getNumberplateRequests(): IObservable<INumberplateRequest[]> {
		return this.numberplateRequests;
	}

	public getNicknameRequests(): IObservable<INicknameRequest[]> {
		return this.nicknameRequests;
	}

	public getAnticheatRequests(): IObservable<IAnticheatRequest[]> {
		return this.anticheatRequests;
	}

	public getScreenshot(): IObservable<string | null> {
		return this.screenshot;
	}

	public async closeScreenshot(): Promise<void> {
		this.screenshot.next(null);
	}

	public async approveNumberplateRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		console.log(
			'[AdminServiceStub] approveNumberplateRequest',
			requestId,
			reason,
		);
		this.numberplateRequests.next(
			this.numberplateRequests.getValue().filter((r) => r.id !== requestId),
		);
		this.syncModerationsCount();
	}

	public async rejectNumberplateRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		console.log(
			'[AdminServiceStub] rejectNumberplateRequest',
			requestId,
			reason,
		);
		this.numberplateRequests.next(
			this.numberplateRequests.getValue().filter((r) => r.id !== requestId),
		);
		this.syncModerationsCount();
	}

	public async approveNicknameRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		console.log('[AdminServiceStub] approveNicknameRequest', requestId, reason);
		this.nicknameRequests.next(
			this.nicknameRequests.getValue().filter((r) => r.id !== requestId),
		);
		this.syncModerationsCount();
	}

	public async rejectNicknameRequest(
		requestId: string,
		reason?: string,
	): Promise<void> {
		console.log('[AdminServiceStub] rejectNicknameRequest', requestId, reason);
		this.nicknameRequests.next(
			this.nicknameRequests.getValue().filter((r) => r.id !== requestId),
		);
		this.syncModerationsCount();
	}

	public async spectateAnticheatRequest(characterId: string): Promise<void> {
		console.log('[AdminServiceStub] spectateAnticheatRequest', characterId);
	}

	public async closePanel(): Promise<void> {
		console.log('[AdminServiceStub] admin:closePanel');
	}

	public async fetchRequests(): Promise<void> {
		console.log('[AdminServiceStub] fetchRequests');
		this.syncModerationsCount();
	}

	public async markAnticheatRequestsViewed(
		requestIds: string[],
	): Promise<void> {
		console.log('[AdminServiceStub] markAnticheatRequestsViewed', requestIds);
		requestIds.forEach((requestId) =>
			this.viewedAnticheatRequestIds.add(requestId),
		);
		this.anticheatCount.next(
			this.anticheatRequests
				.getValue()
				.filter((request) => !this.viewedAnticheatRequestIds.has(request.id))
				.length,
		);
	}

	public async markModerationRequestsViewed({
		numberplateRequestIds,
		nicknameRequestIds,
	}: IAdminMarkModerationRequestsViewed): Promise<void> {
		console.log(
			'[AdminServiceStub] markModerationRequestsViewed',
			numberplateRequestIds,
			nicknameRequestIds,
		);
		numberplateRequestIds.forEach((requestId) =>
			this.viewedModerationRequestIds.add(requestId),
		);
		nicknameRequestIds.forEach((requestId) =>
			this.viewedModerationRequestIds.add(requestId),
		);
		this.syncModerationsCount();
	}

	private syncModerationsCount(): void {
		this.moderationsCount.next(
			this.numberplateRequests
				.getValue()
				.filter((request) => !this.viewedModerationRequestIds.has(request.id))
				.length +
				this.nicknameRequests
					.getValue()
					.filter(
						(request) => !this.viewedModerationRequestIds.has(request.viewId),
					).length,
		);
	}
}
